import { useMemo } from 'react';

import type {
  DataTableColumnVisibilityProps,
  DataTableColumnVisibilityState,
} from './column-visibility-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type {
  Column,
  ColumnDef,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';
import { memo } from '../../tanstack-table-core-override/src/index.js';

/**
 * Helper function to not duplicate this logic for the slightly different use cases of user actions and column settings modal.
 * @internal
 */
export function getCanHide<TData>(
  table: Table<TData>,
  column: Column<TData, unknown>,
  isHideable: (columns: Column<TData, unknown>[]) => boolean,
): boolean {
  // The feature is disabled altogether.
  if (table.options.enableHiding === false) {
    return false;
  }

  // It is a leaf column, just look at the `enableHiding` for this column
  if (!column.columns || column.columns.length === 0) {
    return column.columnDef.enableHiding ?? true;
  }

  // It has sub-columns.
  // `enableHiding` will be false if it's explicitly set to false
  // Otherwise check the provided function
  return column.columnDef.enableHiding === false
    ? false
    : isHideable(column.columns);
}

/**
 * @returns true if *all* child columns can be hidden, false otherwise.
 */
function isParentColumnHideable<TData>(
  childColumns: Column<TData, unknown>[],
): boolean {
  return childColumns.every((c) => c.columnDef.enableHiding);
}

/**
 * Additional feature implementation for column visibility.
 * @internal
 */
export const DataTableColumnVisibility: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    table.getOrderedVisibleLeafColumns = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnPinning,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        return [
          ...table.getLeftVisibleLeafColumns(),
          ...table.getCenterVisibleLeafColumns(),
          ...table.getRightVisibleLeafColumns(),
        ];
      },
      { key: isDev() && 'getOrderedVisibleLeafColumns' },
    );

    // Helper function that provides an array of all visible leaf-node columns, excluding builtin columns.
    table.getVisibleLeafColumnsWithoutBuiltin = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        return table
          .getVisibleLeafColumns()
          .filter((leafColumn) => !leafColumn.columnDef.meta?.builtin);
      },
      {
        key: isDev() && 'getVisibleLeafColumnsWithoutBuiltin',
      },
    );

    // Helper function that provides an array of all center visible leaf-node columns, excluding builtin columns.
    table.getCenterVisibleLeafColumnsWithoutBuiltIn = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnPinning,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        return table
          .getCenterVisibleLeafColumns()
          .filter((leafColumn) => !leafColumn.columnDef.meta?.builtin);
      },
      { key: isDev() && 'getCenterVisibleLeafColumnsWithoutBuildIn' },
    );

    table.getVisibleVirtualColumnIdsWithoutBuiltin = () => {
      const visibleLeafColumns = table.getVisibleLeafColumnsWithoutBuiltin();
      const virtualColumns =
        table.getColumnVirtualizer()?.getVirtualItems() ?? [];

      return virtualColumns.flatMap(
        (virtualItem) => visibleLeafColumns.at(virtualItem.index)?.id ?? [],
      );
    };
  },

  createColumn<TData extends DataTableRowData>(
    column: Column<TData, unknown>,
    table: Table<TData>,
  ) {
    column.getIsLastVisibleColumn = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnPinning,
          table.options.columns,
        ];
      },
      () => {
        // Find the last visible column, prioritizing right-pinned columns, then center columns
        const lastVisibleLeafColumnId =
          table.getRightVisibleLeafColumns().at(-1)?.id ??
          table.getCenterVisibleLeafColumns().at(-1)?.id;

        // return true if the current column or one of its child columns is the last visible column
        return (
          column.id === lastVisibleLeafColumnId ||
          column.columns.some(
            (childColumn) => childColumn.id === lastVisibleLeafColumnId,
          )
        );
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getIsLastVisibleColumn',
      ),
    );

    // Helper function that provides an array of all visible leaf-node columns for this column.
    column.getVisibleLeafColumns = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        return column
          .getLeafColumns()
          .filter((leafColumn) => leafColumn.getIsVisible());
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getVisibleLeafColumns',
      ),
    );

    column.getCenterVisibleLeafColumns = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnPinning,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        return column
          .getVisibleLeafColumns()
          .filter((leafColumn) => !leafColumn.getIsPinned());
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getCenterVisibleLeafColumns',
      ),
    );

    column.getLeftVisibleLeafColumns = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnPinning.left,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        const ids = new Set(column.getVisibleLeafColumns().map((c) => c.id));
        return table.getLeftVisibleLeafColumns().filter((c) => ids.has(c.id));
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getLeftVisibleLeafColumns',
      ),
    );

    column.getRightVisibleLeafColumns = memo(
      () => {
        const state = table.getState();
        return [
          state.columnVisibility,
          state.columnPinning.right,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        const ids = new Set(column.getVisibleLeafColumns().map((c) => c.id));
        return table.getRightVisibleLeafColumns().filter((c) => ids.has(c.id));
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getRightVisibleLeafColumns',
      ),
    );

    column.getCanHide = () => getCanHide(table, column, isParentColumnHideable);
  },
};

/**
 * Configuration hook for the DataTable ColumnVisibility feature.
 * @internal
 */
export function useColumnVisibility<TData extends DataTableRowData>(
  props: DataTableColumnVisibilityProps,
  options: TableOptions<TData>,
): void {
  const columnVisibilityConfig = useDataTableConfigElement('columnVisibility');

  const [columnVisibility, onColumnVisibilityChange] = useControllableState({
    value: props.columnVisibility ?? columnVisibilityConfig,
    defaultValue: props.defaultColumnVisibility,
    onChange: props.onColumnVisibilityChange,
  });

  options.state = { ...options.state, columnVisibility };
  options.onColumnVisibilityChange = onColumnVisibilityChange;
  options.originalColumnVisibility = useMemo(() => {
    return (
      props.defaultColumnVisibility ??
      getColumnVisibilityFromOriginalColumns(options.columns)
    );
  }, [props.defaultColumnVisibility, options.columns]);
}

/**
 * Helper function that generates visibility state from original columns.
 * @internal
 */
function getColumnVisibilityFromOriginalColumns<TData extends DataTableRowData>(
  columnDefs: ColumnDef<TData, unknown>[],
): DataTableColumnVisibilityState {
  const visibilityState: DataTableColumnVisibilityState = {};

  for (const columnDef of columnDefs) {
    if (isGroupColumnDef(columnDef) && columnDef.columns) {
      const subColumnDefs = columnDef.columns;
      for (const subColumnDef of subColumnDefs) {
        if (subColumnDef.id) {
          visibilityState[subColumnDef.id] = true;
        }
      }
    }
    // In our scenarios, we enforce id's on everything, but based on the tanstack types,
    // they could still be undefined, hence the check here.
    else if (columnDef.id) {
      visibilityState[columnDef.id] = true;
    }
  }

  return visibilityState;
}
